import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { BookMarked, FolderPlus, Plus, Check, Loader2 } from 'lucide-react';
import LibraryFreeNote from './LibraryFreeNote';

// Generic "Save to My INASW Library" button usable from any database.
const SaveToLibraryButton = ({ item, saved, onSaved, size = 'sm', variant = 'outline', className = '' }) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [libItemId, setLibItemId] = useState(null);
  const [collections, setCollections] = useState([]);
  const [inCollections, setInCollections] = useState(new Set());
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const loadCollections = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/library/collections`); setCollections(r.data.collections || []); }
    catch (e) { /* ignore */ }
  }, []);

  const handleClick = async () => {
    if (!isAuthenticated) {
      toast.info('Create a free account to save this to your INASW Library.');
      navigate('/login');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/library/save`, item);
      const sa17 = r.data.item;
      setLibItemId(sa17.item_id);
      if (!r.data.already) toast.success('Saved to your INASW Library.');
      if (onSaved) onSaved(item.ref_id);
      await loadCollections();
      setOpen(true);
    } catch (e) { toast.error('Could not save.'); } finally { setBusy(false); }
  };

  const addTo = async (col) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/library/collections/${col.collection_id}/add`, { item_id: libItemId });
      setInCollections((s) => new Set(s).add(col.collection_id));
      toast.success(`Added to "${col.name}".`);
    } catch (e) { toast.error('Could not add.'); } finally { setBusy(false); }
  };

  const createAndAdd = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/library/collections`, { name: newName.trim() });
      const col = r.data.collection;
      await axios.post(`${API_URL}/library/collections/${col.collection_id}/add`, { item_id: libItemId });
      toast.success(`Added to "${col.name}".`);
      setNewName('');
      await loadCollections();
      setInCollections((s) => new Set(s).add(col.collection_id));
    } catch (e) { toast.error('Could not create collection.'); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="inline-flex items-center gap-2 flex-wrap">
        <Button type="button" size={size} variant={variant} onClick={handleClick} disabled={busy}
          className={`${saved ? 'border-secondary text-secondary' : ''} ${className}`}
          data-testid={`save-to-library-${item.item_type}-${item.ref_id}`}>
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <BookMarked className="w-4 h-4 mr-1" />}
          {saved ? 'In Library' : 'Save to Library'}
        </Button>
        <Link to="/my-library" className="text-xs font-semibold text-secondary hover:underline whitespace-nowrap"
          data-testid={`view-my-library-${item.item_type}-${item.ref_id}`}>
          View My INASW Library
        </Link>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="library-collection-dialog">
          <DialogHeader><DialogTitle>Add to a Collection (optional)</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">Saved to your Library. Organize it into a collection like "Hospice Admission Packet" or "APS Cases".</p>
          <LibraryFreeNote variant="banner" />
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {collections.length === 0 && <p className="text-sm text-gray-400">No collections yet — create one below.</p>}
            {collections.map((c) => (
              <button key={c.collection_id} onClick={() => addTo(c)} disabled={busy}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg border hover:border-primary text-left"
                data-testid={`library-collection-option-${c.collection_id}`}>
                <span className="text-gray-800 text-sm">{c.name} <span className="text-xs text-gray-400">({c.item_count})</span></span>
                {inCollections.has(c.collection_id) ? <Check className="w-4 h-4 text-green-600" /> : <Plus className="w-4 h-4 text-gray-400" />}
              </button>
            ))}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Input placeholder="New collection name" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="library-new-collection-name" />
            <Button className="bg-primary text-white" onClick={createAndAdd} disabled={busy || !newName.trim()} data-testid="library-create-collection">
              <FolderPlus className="w-4 h-4 mr-1" /> Create &amp; Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SaveToLibraryButton;
